import { Flex, Spacer } from '@chakra-ui/react'
import {
  Button,
  ChevronDownIcon,
  ChevronUpIcon,
  InputContainer,
  MarkdownEditor,
  Menu,
  NumberInput,
  Panel,
  ReadOnlyProperty,
  SparklesIcon,
  Text,
} from 'components'
import { useWorkspace } from 'components/providers'
import { useState } from 'react'
import { AIConfig } from 'types'
import { useWorkspacePrompt } from 'utils'

export default function AIPanel() {
  const { workspace, updateWorkspace } = useWorkspace()
  const workspacePrompt = useWorkspacePrompt()
  const [expandPrompt, setExpandPrompt] = useState(false)
  const temperature = workspace?.aiConfig?.temperature ?? 0.3
  const promptLineLimit = expandPrompt ? undefined : 2

  async function updateWorkspaceAIConfig(param: Partial<AIConfig>) {
    await updateWorkspace({ aiConfig: { ...workspace?.aiConfig, ...param } })
  }

  function toggleExpandPrompt() {
    setExpandPrompt(!expandPrompt)
  }

  return (
    <Panel p={5} gap={4}>
      <Flex gap={3} w="100%" align="center">
        <SparklesIcon color="primary.500" />
        <Flex direction="column">
          <Text textStyle="h6">AI Summaries</Text>
          <Text textStyle="bodyXSmall" opacity={0.5}>
            Customize AI parameters for event summaries
          </Text>
        </Flex>
        <Spacer />
      </Flex>
      <Flex direction="column" gap={4}>
        <Flex gap={1} direction="column">
          <Flex gap={6} direction={{ base: 'column', md: 'row' }}>
            <InputContainer label="Model">
              <Menu
                variant="outline"
                value={workspace?.aiConfig?.model || 'gemini-2.5-flash'}
                options={[
                  { value: 'gemini-2.5-flash', label: 'Gemini 2.5 Flash' },
                  { value: 'gemini-2.5-flash-lite', label: 'Gemini 2.5 Flash Lite' },
                  { value: 'gemini-2.5-pro', label: 'Gemini 2.5 Pro' },
                ]}
                onChange={value => updateWorkspaceAIConfig({ model: value })}
                menuProps={{
                  matchWidth: true,
                }}
              />
            </InputContainer>

            <InputContainer label="Temperature">
              <NumberInput
                value={temperature}
                onChange={value => updateWorkspaceAIConfig({ temperature: value })}
                min={0}
                max={1}
                step={0.1}
              />
              <Text textStyle="bodySmall" opacity={0.5}>
                {temperature === 0
                  ? 'Deterministic'
                  : temperature < 0.5
                  ? temperature === 0.3
                    ? 'Focused (Recommended)'
                    : 'Focused'
                  : temperature < 1
                  ? 'Balanced'
                  : 'Creative'}
              </Text>
            </InputContainer>
          </Flex>
          <InputContainer label="Prompt">
            <Menu
              variant="outline"
              value={workspace?.useCase}
              options={[
                { label: 'Sales', value: 'sales' },
                { label: 'Recruiting', value: 'recruiting' },
              ]}
              onChange={value => updateWorkspace({ useCase: value })}
              menuProps={{
                matchWidth: true,
              }}
            />
            <ReadOnlyProperty
              value={
                <Flex gap={1}>
                  <MarkdownEditor lineClamp={promptLineLimit} readOnly value={workspacePrompt} />
                  <Button variant="ghost" onClick={toggleExpandPrompt}>
                    {expandPrompt ? <ChevronUpIcon /> : <ChevronDownIcon />}
                  </Button>
                </Flex>
              }
              textProps={{
                fontWeight: '600',
                opacity: 0.6,
              }}
            />
          </InputContainer>
        </Flex>
      </Flex>
    </Panel>
  )
}
